import { Client, ClientError, isCurrentTurnBoundaryEvent } from "eve/client";
import type {
  HandleMessageStreamEvent,
  InputResponse,
  MessageResponse,
  SendTurnPayload,
  SessionState,
} from "eve/client";
import {
  answerableQuestions,
  compactLog,
  isFreeform,
  projectLog,
  reduceThread,
  transcriptRecap,
  type ThreadLogEvent,
  type ThreadProjection,
} from "./projection";

/**
 * Persisted eve cursor for one conversation.
 * - `inFlight`: a turn was accepted but this client never saw it settle
 *   (refresh / navigation mid-reply) — the next mount reconnects to it.
 * - `v: 2`: `streamIndex` is exact. Older rows could carry a cursor computed
 *   off the accumulated multi-session log, which points past the real stream.
 */
export interface ThreadCursor extends SessionState {
  inFlight?: boolean;
  v?: 2;
}

/**
 * - `submitting`: the turn POST is out, no stream event yet.
 * - `streaming`: events are arriving.
 * - `reconnecting`: re-attaching to a turn that was already running.
 */
export type ThreadStatus = "idle" | "submitting" | "streaming" | "reconnecting";

export interface ThreadSnapshot {
  projection: ThreadProjection;
  status: ThreadStatus;
  /** The user's message until eve confirms it (`message.received`). */
  outgoing: { text: string; failed: boolean } | null;
  /** Answers being delivered, by requestId. */
  answering: Readonly<Record<string, InputResponse>>;
  error: string | null;
  /** The last save was rejected — this thread is not reaching history. */
  persistFailed: boolean;
}

export interface ThreadCallbacks {
  /** Persist cursor + compacted log; resolves false when the save was rejected. */
  save: (state: { session: ThreadCursor; events: ThreadLogEvent[] }) => Promise<boolean>;
  /** A turn settled — Amy may have changed campaigns. */
  onSettled?: () => void;
  /** The queued first message of a new chat went out. */
  onFirstMessageSent?: () => void;
}

export interface ThreadControllerOptions extends ThreadCallbacks {
  host: string;
  getToken: () => Promise<string | null>;
  initialSession: ThreadCursor | null;
  initialEvents: readonly ThreadLogEvent[];
  readOnly: boolean;
  /** First message of a just-created chat, sent once on attach. */
  firstMessage?: string | null;
}

const SAVE_THROTTLE_MS = 1500;

/**
 * Drives one Amy conversation against the eve service with eve's low-level
 * `Client` — `useEveAgent` can neither reconnect to a turn that was running
 * before a refresh nor persist mid-turn, which eve's own docs leave to the
 * lower-level client.
 *
 * Invariants:
 * - The persisted `{cursor, log}` pair is always taken from the same moment,
 *   so the cursor never points past the saved events; a refresh loses at
 *   most one throttle window, which reconnecting re-reads from eve's durable
 *   stream.
 * - The cursor counts STREAM events of the current eve session only;
 *   client-authored log entries (answers) never advance it.
 * - A conversation whose eve session can no longer continue rolls over to a
 *   fresh session, carrying the visible transcript as one-turn context.
 */
export class ThreadController {
  #opts: ThreadControllerOptions;
  #client: Client;
  #log: ThreadLogEvent[];
  #cursor: ThreadCursor;
  #snapshot: ThreadSnapshot;
  #listeners = new Set<() => void>();
  #stream: AbortController | null = null;
  #attachTimer: ReturnType<typeof setTimeout> | null = null;
  #saveTimer: ReturnType<typeof setTimeout> | null = null;
  #saving = false;
  #saveAgain = false;
  #firstMessage: string | null;

  constructor(opts: ThreadControllerOptions) {
    this.#opts = opts;
    // Credentials are re-resolved before every request and reconnect —
    // Clerk's short token TTL is fine.
    this.#client = new Client({
      host: opts.host,
      auth: { bearer: async () => (await opts.getToken()) ?? "" },
    });
    this.#log = [...opts.initialEvents];
    this.#cursor = normalizeCursor(opts.initialSession, this.#log);
    this.#firstMessage = opts.firstMessage ?? null;
    this.#snapshot = {
      projection: projectLog(this.#log),
      status: "idle",
      outgoing: null,
      answering: {},
      error: null,
      persistFailed: false,
    };
  }

  // -- external store ------------------------------------------------------

  subscribe = (listener: () => void): (() => void) => {
    this.#listeners.add(listener);
    return () => this.#listeners.delete(listener);
  };

  getSnapshot = (): ThreadSnapshot => this.#snapshot;

  setCallbacks(callbacks: ThreadCallbacks): void {
    this.#opts = { ...this.#opts, ...callbacks };
  }

  get busy(): boolean {
    return this.#snapshot.status !== "idle";
  }

  #set(patch: Partial<ThreadSnapshot>): void {
    this.#snapshot = { ...this.#snapshot, ...patch };
    for (const listener of this.#listeners) listener();
  }

  // -- lifecycle -------------------------------------------------------------

  /**
   * Mount. Work starts one tick later so React StrictMode's
   * mount → unmount → mount never sends or reconnects twice.
   */
  attach(): void {
    if (this.#opts.readOnly || this.#attachTimer) return;
    this.#attachTimer = setTimeout(() => {
      this.#attachTimer = null;
      if (this.#stream) return;
      if (this.#firstMessage) {
        const message = this.#firstMessage;
        this.#firstMessage = null;
        this.#opts.onFirstMessageSent?.();
        void this.send(message);
        return;
      }
      if (this.#needsReconnect()) void this.#reconnect();
    }, 0);
  }

  /**
   * Unmount: stop listening and flush the save. The turn keeps running on
   * the server; the persisted `inFlight` cursor reconnects to it next time.
   */
  detach(): void {
    if (this.#attachTimer) {
      clearTimeout(this.#attachTimer);
      this.#attachTimer = null;
    }
    this.#stream?.abort();
    this.#stream = null;
    if (this.#saveTimer) this.#saveNow();
  }

  // -- commands --------------------------------------------------------------

  async send(text: string): Promise<void> {
    const message = text.trim();
    if (!message || this.#opts.readOnly || this.busy) return;
    // While one freeform question is parked, eve consumes a plain message as
    // its answer — send it as one, so the question settles as answered.
    const parked = answerableQuestions(this.#snapshot.projection);
    if (parked.length === 1 && isFreeform(parked[0]!.request)) {
      return this.answer(parked[0]!.id, { text: message });
    }
    this.#set({ outgoing: { text: message, failed: false }, error: null });
    await this.#turn({ message }, { canRollOver: true });
  }

  /** Resend a message whose delivery failed. */
  async retry(): Promise<void> {
    const outgoing = this.#snapshot.outgoing;
    if (!outgoing?.failed || this.busy) return;
    this.#set({ outgoing: null });
    await this.send(outgoing.text);
  }

  async answer(requestId: string, response: { optionId?: string; text?: string }): Promise<void> {
    return this.answerMany([{ requestId, ...response }]);
  }

  /** Answer several parked questions in ONE send — eve resumes only once
   * every question of a park is answered (a questionnaire's batch). */
  async answerMany(responses: InputResponse[]): Promise<void> {
    if (this.#opts.readOnly || this.busy || responses.length === 0) return;
    const answering = { ...this.#snapshot.answering };
    for (const r of responses) answering[r.requestId] = r;
    this.#set({ answering, error: null });
    const parked = answerableQuestions(this.#snapshot.projection);
    const recap = responses.flatMap((r) => {
      const question = parked.find((q) => q.id === r.requestId);
      const label =
        (r.optionId && question?.request.options?.find((o) => o.id === r.optionId)?.label) ||
        r.text ||
        r.optionId;
      return question && label ? [`${question.request.prompt}\n\nMy answer: ${label}`] : [];
    });
    await this.#turn(
      { inputResponses: responses },
      {
        // A dead session can't take the answers — and the parked questions
        // would block the composer forever — so a fresh session gets them as
        // a plain message, with the questions for context.
        rollOverAs: recap.length > 0 ? recap.join("\n\n---\n\n") : undefined,
        // Record the answers in the log once eve accepted them — the stream
        // itself never carries responses.
        onAccepted: () =>
          this.#append({
            type: "client.input.responded",
            data: { responses, at: new Date().toISOString() },
          }),
      }
    );
    const rest = { ...this.#snapshot.answering };
    for (const r of responses) delete rest[r.requestId];
    this.#set({ answering: rest });
  }

  /** Stop the running turn server-side; the stream then settles it with
   * `turn.cancelled`. Aborting locally is only the fallback. */
  async stop(): Promise<void> {
    if (!this.busy) return;
    if (this.#cursor.sessionId) {
      try {
        const { status } = await this.#client.session(clientCursor(this.#cursor)).cancel();
        // Accepted: `turn.cancelled` arrives through the stream and settles it.
        // Nothing running server-side: stop waiting for a stream that won't end.
        if (status === "accepted") return;
      } catch (e) {
        console.error("Amy: cancel failed", e);
      }
    }
    this.#stream?.abort();
  }

  // -- streaming -------------------------------------------------------------

  #needsReconnect(): boolean {
    if (!this.#cursor.sessionId) return false;
    if (this.#cursor.inFlight) return true;
    for (let i = this.#log.length - 1; i >= 0; i--) {
      const event = this.#log[i]!;
      if (event.type.startsWith("client.")) continue;
      return !isCurrentTurnBoundaryEvent(event as HandleMessageStreamEvent);
    }
    return false;
  }

  async #turn(
    payload: SendTurnPayload,
    opts: {
      canRollOver?: boolean;
      /** Roll over by sending this message instead of the payload. */
      rollOverAs?: string;
      onAccepted?: () => void;
    }
  ): Promise<void> {
    const ctrl = new AbortController();
    this.#stream = ctrl;
    this.#set({ status: "submitting" });
    let accepted = false;
    let settled = false;
    try {
      let response: MessageResponse;
      try {
        response = await this.#client
          .session(clientCursor(this.#cursor))
          .send({ ...payload, signal: ctrl.signal });
      } catch (e) {
        const canRollOver = opts.canRollOver || opts.rollOverAs !== undefined;
        if (!canRollOver || !this.#cursor.sessionId || !isDeadSession(e)) throw e;
        // The eve session is gone (expired, or the service lost its state) —
        // continue in a fresh one with the conversation so far as context.
        console.warn("Amy: session can't continue, starting a new one", e);
        this.#cursor = { streamIndex: 0, v: 2 };
        const recap = transcriptRecap(this.#snapshot.projection);
        response = await this.#client.session().send({
          ...(opts.rollOverAs !== undefined ? { message: opts.rollOverAs } : payload),
          ...(recap && {
            clientContext: `Earlier in this conversation (the previous session can no longer continue):\n\n${recap}`,
          }),
          signal: ctrl.signal,
        });
      }
      accepted = true;
      if (response.sessionId !== this.#cursor.sessionId) {
        this.#cursor = { sessionId: response.sessionId, streamIndex: 0, v: 2 };
      }
      // From here the turn runs server-side: persist the cursor right away so
      // a refresh before the first event still reconnects to it.
      this.#cursor = { ...this.#cursor, inFlight: true };
      opts.onAccepted?.();
      this.#saveNow();
      for await (const event of response) {
        if (ctrl.signal.aborted) break;
        this.#ingest(event);
        if (isCurrentTurnBoundaryEvent(event)) settled = true;
      }
    } catch (e) {
      if (ctrl.signal.aborted) return;
      console.error("Amy: turn failed", e);
      if (!accepted) {
        const outgoing = this.#snapshot.outgoing;
        this.#set({
          outgoing: outgoing && "message" in payload ? { ...outgoing, failed: true } : outgoing,
          error:
            "inputResponses" in payload
              ? "Couldn't send your answer — try again."
              : describeSendError(e),
        });
      }
    } finally {
      if (this.#stream === ctrl) {
        this.#stream = null;
        this.#finishTurn(settled);
        // The stream broke mid-turn: re-attach once and pick up where it left.
        if (accepted && !settled && !ctrl.signal.aborted) void this.#reconnect();
      }
    }
  }

  /** Re-attach to a turn that was running before this mount (or whose stream broke). */
  async #reconnect(): Promise<void> {
    const ctrl = new AbortController();
    this.#stream = ctrl;
    this.#set({ status: "reconnecting", error: null });
    let settled = false;
    try {
      const session = this.#client.session(clientCursor(this.#cursor));
      for await (const event of session.stream({ signal: ctrl.signal })) {
        if (ctrl.signal.aborted) return;
        if (this.#snapshot.status === "reconnecting") this.#set({ status: "streaming" });
        this.#ingest(event);
        if (isCurrentTurnBoundaryEvent(event)) {
          settled = true;
          break;
        }
      }
    } catch (e) {
      if (ctrl.signal.aborted) return;
      console.error("Amy: reconnect failed", e);
      this.#set({ error: "Couldn't reconnect to Amy's reply. Send a message to continue." });
    } finally {
      if (this.#stream === ctrl) {
        this.#stream = null;
        // Nothing more is coming either way — don't retry this on every mount.
        this.#finishTurn(true);
        if (!settled) console.warn("Amy: reconnect ended without a turn boundary");
      }
    }
  }

  #finishTurn(settled: boolean): void {
    if (settled) {
      this.#cursor = { ...this.#cursor, inFlight: false };
      // Superseded streaming snapshots are dead weight in memory too.
      this.#log = compactLog(this.#log);
    }
    this.#set({ status: "idle" });
    this.#saveNow();
    if (settled) this.#opts.onSettled?.();
  }

  #ingest(event: HandleMessageStreamEvent): void {
    this.#cursor = advanceCursor(this.#cursor, event);
    this.#log.push(event);
    const patch: Partial<ThreadSnapshot> = {
      projection: reduceThread(this.#snapshot.projection, event),
    };
    if (this.#snapshot.status === "submitting") patch.status = "streaming";
    if (event.type === "message.received" && this.#snapshot.outgoing) patch.outgoing = null;
    this.#set(patch);
    if (isCurrentTurnBoundaryEvent(event)) this.#saveNow();
    else this.#saveSoon();
  }

  /** Append a client-authored log entry (never advances the stream cursor). */
  #append(event: ThreadLogEvent): void {
    this.#log.push(event);
    this.#set({ projection: reduceThread(this.#snapshot.projection, event) });
  }

  // -- persistence -----------------------------------------------------------

  #saveSoon(): void {
    if (this.#saveTimer) return;
    this.#saveTimer = setTimeout(() => {
      this.#saveTimer = null;
      void this.#persist();
    }, SAVE_THROTTLE_MS);
  }

  #saveNow(): void {
    if (this.#saveTimer) {
      clearTimeout(this.#saveTimer);
      this.#saveTimer = null;
    }
    void this.#persist();
  }

  /** One save in flight at a time; a save requested meanwhile runs after it
   * with the then-current state. */
  async #persist(): Promise<void> {
    if (this.#opts.readOnly) return;
    if (this.#saving) {
      this.#saveAgain = true;
      return;
    }
    this.#saving = true;
    try {
      const ok = await this.#opts.save({ session: this.#cursor, events: compactLog(this.#log) });
      if (ok === this.#snapshot.persistFailed) this.#set({ persistFailed: !ok });
    } finally {
      this.#saving = false;
      if (this.#saveAgain) {
        this.#saveAgain = false;
        void this.#persist();
      }
    }
  }
}

// ---------------------------------------------------------------------------

const clientCursor = (c: ThreadCursor): SessionState => ({
  sessionId: c.sessionId,
  continuationToken: c.continuationToken,
  streamIndex: c.streamIndex,
});

function advanceCursor(cursor: ThreadCursor, event: HandleMessageStreamEvent): ThreadCursor {
  const streamIndex = cursor.streamIndex + 1;
  switch (event.type) {
    case "session.waiting":
      return { ...cursor, streamIndex, continuationToken: event.data.continuationToken };
    case "session.completed":
    case "session.failed":
      // A finished session takes no more input — the next send starts fresh.
      return { streamIndex: 0, v: 2 };
    default:
      return { ...cursor, streamIndex };
  }
}

/**
 * Pre-v2 rows could persist a cursor computed off the accumulated log, which
 * spans every eve session of the thread; the server's stream restarts at 0 on
 * each `session.started`, so such a cursor resumes deaf. Those logs were
 * never compacted, so the true cursor is the event count since the last
 * `session.started`.
 */
function normalizeCursor(saved: ThreadCursor | null, log: readonly ThreadLogEvent[]): ThreadCursor {
  if (!saved?.sessionId) return { streamIndex: 0, v: 2 };
  if (saved.v === 2) return saved;
  for (let i = log.length - 1; i >= 0; i--) {
    if (log[i]!.type === "session.started") return { ...saved, streamIndex: log.length - i, v: 2 };
  }
  return { ...saved, v: 2 };
}

/** The send hit a session eve can no longer continue. */
function isDeadSession(e: unknown): boolean {
  if (!(e instanceof ClientError)) return false;
  return e.status === 404 || e.status === 410 || /not found|no longer|expired/i.test(e.body);
}

function describeSendError(e: unknown): string {
  if (e instanceof ClientError) {
    if (e.status === 401 || e.status === 403) return "You're not signed in to Amy — refresh the page and try again.";
    if (e.status === 409 || /already processing/i.test(e.body)) return "Amy is still working on the previous message.";
    if (e.status >= 500) return "Amy is unavailable right now — try again in a moment.";
  }
  if (e instanceof TypeError) return "Couldn't reach Amy — check your connection and try again.";
  return (e as Error)?.message || "Couldn't send your message.";
}
